import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "./AuthProvider";
import { friendlyMessage, listIssues } from "./projectApi";
import { listDocuments, myReviewInbox } from "./eqmsApi";
import { ISSUE_STATUS_CATEGORY } from "../models/project";
import type { Project } from "../models/account";
import type { DocReviewTask } from "../models/eqms";

/** The per-project counters the 홈 folder cards show. */
export interface ProjectStats {
  issues: number;
  openIssues: number;
  documents: number;
  docsInReview: number;
}

const EMPTY_STATS: ProjectStats = { issues: 0, openIssues: 0, documents: 0, docsInReview: 0 };

/** Enough of a document to label a review task without a second round-trip. */
export interface DocRef {
  docNumber: string;
  title: string;
  projectId: string;
  projectName: string;
}

// Data for the 홈 landing screen: one roll-up per project in the current workspace, plus the reviews
// waiting on this person.
//
// There is no server-side aggregate for this, so the counters are derived from the same per-project
// list endpoints the project screens already use. Each project's two calls run in parallel and a
// failing one degrades to zero rather than blanking the page — a project the caller cannot read must
// not cost them the whole screen. Those same document lists double as the index that turns a review
// task's bare documentId into a real 문서번호 · 제목, so the inbox never shows a raw id.
export function useHome(projects: Project[]) {
  const { status, token } = useAuth();
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const getToken = useMemo(() => () => tokenRef.current(), []);

  // The effect keys off the project ids; the ref hands the callback the current objects without
  // making a new array identity re-run the whole fan-out.
  const projectsRef = useRef(projects);
  projectsRef.current = projects;
  const projectKey = projects.map((p) => p.id).join(",");

  const [stats, setStats] = useState<Record<string, ProjectStats>>({});
  const [docIndex, setDocIndex] = useState<Record<string, DocRef>>({});
  const [reviewTasks, setReviewTasks] = useState<DocReviewTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const current = projectsRef.current;
      const [inbox, perProject] = await Promise.all([
        myReviewInbox(getToken).catch(() => [] as DocReviewTask[]),
        Promise.all(current.map(async (p) => {
          const [issues, docs] = await Promise.all([
            listIssues(getToken, p.id).catch(() => []),
            listDocuments(getToken, p.id).catch(() => []),
          ]);
          return { project: p, issues, docs };
        })),
      ]);

      const nextStats: Record<string, ProjectStats> = {};
      const nextDocs: Record<string, DocRef> = {};
      for (const { project, issues, docs } of perProject) {
        nextStats[project.id] = {
          issues: issues.length,
          openIssues: issues.filter((i) => ISSUE_STATUS_CATEGORY[i.status] !== "DONE").length,
          documents: docs.length,
          docsInReview: docs.filter((d) => d.status === "IN_REVIEW").length,
        };
        for (const d of docs) {
          nextDocs[d.id] = {
            docNumber: d.docNumber,
            title: d.title,
            projectId: project.id,
            projectName: project.name,
          };
        }
      }
      setStats(nextStats);
      setDocIndex(nextDocs);
      setReviewTasks(inbox.filter((t) => t.action === "PENDING"));
    } catch (e) {
      setError(friendlyMessage(e));
    } finally {
      setLoading(false);
    }
    // projectKey stands in for the project list (see projectsRef above).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getToken, projectKey]);

  useEffect(() => {
    if (status === "authenticated") void reload();
  }, [status, reload]);

  /** Workspace-wide totals for the tiles at the top of 홈. */
  const totals = useMemo(() => {
    const all = Object.values(stats);
    return {
      projects: projects.length,
      openIssues: all.reduce((n, s) => n + s.openIssues, 0),
      documents: all.reduce((n, s) => n + s.documents, 0),
      pendingReviews: reviewTasks.length,
    };
  }, [stats, reviewTasks, projects.length]);

  return {
    stats,
    statsFor: (projectId: string) => stats[projectId] ?? EMPTY_STATS,
    docIndex,
    reviewTasks,
    totals,
    loading,
    error,
    clearError: () => setError(null),
    reload,
  };
}

export type HomeController = ReturnType<typeof useHome>;
